import { Skeleton, SkeletonText } from '../Skeleton'
import { cx } from '../cx'

export type LoadingShape = 'list' | 'table' | 'prose' | 'detail' | 'form'

export interface LoadingStateProps {
  readonly shape: LoadingShape
  readonly label?: string | undefined
  readonly rows?: number | undefined
  readonly className?: string | undefined
}

function ListShape({ rows }: { readonly rows: number }) {
  return (
    <div className="flex flex-col">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 border-b border-border py-3 last:border-b-0"
        >
          <Skeleton height="h-3.5" width="w-1/3" />
          <Skeleton height="h-3.5" width="w-16" className="ml-auto" />
        </div>
      ))}
    </div>
  )
}

function TableShape({ rows }: { readonly rows: number }) {
  return (
    <div className="flex flex-col">
      <div className="flex gap-3 border-b border-border py-2">
        <Skeleton height="h-2.5" width="w-24" />
        <Skeleton height="h-2.5" width="w-20" />
        <Skeleton height="h-2.5" width="w-16" className="ml-auto" />
      </div>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex gap-3 border-b border-border py-3 last:border-b-0">
          <Skeleton height="h-3.5" width="w-1/4" />
          <Skeleton height="h-3.5" width="w-1/5" />
          <Skeleton height="h-3.5" width="w-14" className="ml-auto" />
        </div>
      ))}
    </div>
  )
}

function ProseShape({ rows }: { readonly rows: number }) {
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <Skeleton height="h-6" width="w-2/5" />
      <SkeletonText lines={rows} />
      <SkeletonText lines={Math.max(2, rows - 1)} />
    </div>
  )
}

function DetailShape() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton height="h-6" width="w-1/3" />
      <div className="flex gap-2">
        <Skeleton height="h-5" width="w-20" />
        <Skeleton height="h-5" width="w-24" />
      </div>
      <SkeletonText lines={4} className="max-w-2xl" />
    </div>
  )
}

function FormShape({ rows }: { readonly rows: number }) {
  return (
    <div className="flex max-w-md flex-col gap-4">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex flex-col gap-1.5">
          <Skeleton height="h-3" width="w-24" />
          <Skeleton height="h-8" />
        </div>
      ))}
    </div>
  )
}

export function LoadingState({
  shape,
  label = 'Loading',
  rows = 4,
  className,
}: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="loading-state"
      className={cx('w-full', className)}
    >
      <span className="sr-only">{label}</span>
      {shape === 'list' ? <ListShape rows={rows} /> : null}
      {shape === 'table' ? <TableShape rows={rows} /> : null}
      {shape === 'prose' ? <ProseShape rows={rows} /> : null}
      {shape === 'detail' ? <DetailShape /> : null}
      {shape === 'form' ? <FormShape rows={rows} /> : null}
    </div>
  )
}
